// @flow
import type {
  ConversationNode,
  EditableGraph,
  Graph,
  GraphDetails,
} from 'src/types/chatbot';
import type {Validation} from 'src/utils/validation';
import type {Validation as Validation2} from 'src/utils/validation-2';
import type {EntityType} from 'src/types/ats-entities';
import type {LATQueueEntry} from 'src/types/live-agent-transfer';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {useDispatch, useSelector} from 'react-redux';
import intersectionBy from 'lodash/intersectionBy';

// $FlowFixMe
import {convertFromRaw} from 'draft-js';
import {useReleaseFlag} from 'src/hooks/product-flags';

import {Link as RerouterLink} from 'src/rerouter';

import {handleHideQuestionSuggestion} from './reducer';

import {hasError} from 'src/utils/validation-2';
import {textDraftToString} from 'src/utils/draft';
import {useSmsContext} from './sms-context';
import {useOWBFields} from './hooks';
import {useMutableFieldStatus} from './api-hooks';
import {showGenericConfirm} from 'src/action-creators/modal';
import {bufferList} from 'src/utils/intl';
import classify from 'src/utils/classify';
import {editableNodeTypes, getChildEdges, findParents} from './utils';

import {useWritebackAllowedAttributesV2} from 'src/hooks/useWritebackAllowedAttributes';
import {updateFlowConfig} from 'src/components/conversation-builder/thunks';
import {
  selectQuestionSimilarity,
  selectPresetQuestions,
} from 'src/selectors/chatbot';

import {useApi} from 'src/hooks/useApi';

import {useChatbotStateContext} from './chatbot-state-context';

import {ContextMenu2, MenuOption} from 'src/components/lib/context-menu';
import {OptOutMessage} from './common.jsx';

import WarningIcon from 'src/images/icons/warning-icon.svg';
import {
  MouseTip,
  useContextTip,
} from 'src/components/lib/mouse-tip/mouse-tip.jsx';

import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {ReadOnlyDraftEditor} from './draft-editor.jsx';

import css from './conversation-node.css';


export default function ConversationNodeView({
  node,
  graph,
  flows,
  flowsLoaded,
  style,
  isJumpable,
  isFirstMessage,
  onRender,
  onSize,
  disabled,
  edgeError,
  nodeError,
  nodeWarning,
  entityType,
  isJourneyPreview,
}: {
  node: ConversationNode,
  graph: EditableGraph,
  flows: GraphDetails[],
  flowsLoaded: boolean,
  style: {
    left: number,
    top: number,
  },
  isJumpable: mixed,
  isFirstMessage: boolean,
  onRender: (ConversationNode) => mixed,
  onSize: (ConversationNode, number) => mixed,
  disabled?: mixed,
  edgeError: ?Validation,
  nodeError: ?Validation,
  nodeWarning: ?Validation2,
  entityType: EntityType,
  isJourneyPreview: boolean,
}): React.Node {
  const ref = React.useRef();
  const isSms = useSmsContext();
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  const {state, dispatch} = useChatbotStateContext();

  const enableQuestionSuggestions =
    useReleaseFlag('chatbotQuestionSuggestions') &&
    graph.bot_type === 'task_oriented' &&
    graph.status === 'init' &&
    !state?.hideQuestionSuggestions.includes(String(graph.id));

  onRender(node);
  React.useLayoutEffect(() => {
    onSize(node, ref.current?.offsetHeight || 0);
  }, [isSms, node, isFirstMessage]);

  const conditionsErrors = edgeError?.conditions?.errors;

  const {error, warning} = defineErrors(
    conditionsErrors,
    nodeError,
    nodeWarning,
    t,
  );

  const showError = error.length > 0 || warning.length > 0;

  const matchingQuestion = useQuestionSuggestion(node);

  // This is a somewhat-temporary change to prevent deleting required
  // job id disposition nodes for application bots. This is a very
  // coarse, but specific, set of rules here.
  const undeletable =
    node.name === 'Job Order ID' &&
    node.type === 'disposition' &&
    graph.use_case === 'application_inbound_web';

  let body;
  switch (node.type) {
    case 'summary':
      body = <SummaryBody node={node} graph={graph} />;
      break;
    case 'message':
    case 'file_upload':
    case 'scheduler':
    case 'text_to_apply':
      body = (
        <ReadOnlyDraftEditor
          entityType={entityType}
          isFirstSmsMessage={isFirstMessage && isSms}
          rte_json={node.text_draft_json}
        />
      );
      break;
    case 'question':
      body =
        node.text_draft_json != null &&
        node.text_draft_json.entityMap != null &&
        Array.isArray(node.text_draft_json.blocks) ? (
          <ReadOnlyDraftEditor
            entityType={entityType}
            isFirstSmsMessage={isFirstMessage && isSms}
            rte_json={node.text_draft_json}
          />
        ) : (
          <DraftTextBody
            node={node}
            isSms={isSms}
            isFirstMessage={isFirstMessage}
          />
        );
      break;
    case 'notification':
      body = (
        <ReadOnlyDraftEditor
          entityType={entityType}
          isFirstSmsMessage={isFirstMessage && isSms}
          rte_json={node.notification_email_subject}
        />
      );
      break;
    case 'condition':
      body = <ConditionBody node={node} graph={graph} />;
      break;
    case 'disposition':
      body = <DispositionBody node={node} entityType={entityType} />;
      break;
    case 'job_match':
      body = <JobMatchBody node={node} />;
      break;
    case 'live_agent_transfer':
      body = <LATBody node={node} graph={graph} />;
      break;
    case 'link_to_flow':
      body = (
        <LinkToFlowBody
          node={node}
          graph={graph}
          flows={flows}
          loaded={flowsLoaded}
        />
      );
      break;
    case 'dynamic_question':
      body = <DynamicQuestionBody graph={graph} node={node} />;
      break;
    default:
      body = (
        <DraftTextBody
          node={node}
          isSms={isSms}
          isFirstMessage={isFirstMessage}
        />
      );
      break;
  }

  return (
    <div
      className={classify(
        css.node,
        node.editable ? css.nodeEnabled : css.nodeDisabled,
        isJumpable && css.jumpable,
        error.length > 0 && css.error,
        warning.length > 0 && css.warning,
        enableQuestionSuggestions && matchingQuestion && css.suggestion,
      )}
      ref={ref}
      style={style}
      onClick={
        isJumpable
          ? () =>
              dispatch({
                type: 'ADD_JUMP',
                payload: {
                  nodeId: node.id,
                },
              })
          : null
      }
      // NOTE (kyle): this is required to prevent the jump field
      // from prematurely blurring.
      onMouseDown={isJumpable ? (event) => event.preventDefault() : null}
    >
      <div className={css.title}>
        <span className={css.name}>
          {node.type === 'link_to_flow'
            ? t('Link_to_External_Flow', 'Link to External Flow')
            : node.name}
        </span>
        {showError && (
          <MouseTip content={<AlertList errors={error} warnings={warning} />}>
            <div className={classify(css.warningAlert)}>
              <WarningIcon style={{fontSize: 24}} />
            </div>
          </MouseTip>
        )}
        {!isJumpable &&
          node.editable &&
          (graph.bot_type === 'state_machine' ||
            (graph.bot_type === 'task_oriented' && !isJourneyPreview)) && (
            <ContextMenu2
              className={css.edit}
              menuContent={
                <NodeActions
                  node={node}
                  disabled={disabled || undeletable}
                  hasMultipleChildren={getChildEdges(graph, node.id).length > 1}
                  dispatch={dispatch}
                />
              }
            />
          )}
      </div>
      {isJourneyPreview ? (
        <div className={css.body}>{body}</div>
      ) : (
        <WithQuestionSuggestion
          node={node}
          dispatch={dispatch}
          enableQuestionSuggestions={enableQuestionSuggestions}
          flowId={graph.id}
        >
          <div className={css.body}>{body}</div>
        </WithQuestionSuggestion>
      )}
    </div>
  );
}

const useQuestionSuggestion = (node) => {
  const questionSimilarity = useSelector(selectQuestionSimilarity) ?? {};
  const presetQuestions = useSelector(selectPresetQuestions) ?? [];

  const isCustomQuestion = node.type === 'question' && !node.preset_question_id;
  const questionString =
    isCustomQuestion &&
    node.text_draft_json &&
    textDraftToString(node.text_draft_json);
  const hasMatchingPresetQuestion =
    questionSimilarity[questionString]?.length > 0 &&
    intersectionBy(
      questionSimilarity[questionString],
      presetQuestions,
      ({nlu_entity_type, intent}) => intent + nlu_entity_type,
    ).length > 0;
  const matchingQuestion =
    hasMatchingPresetQuestion && questionSimilarity[questionString][0];

  return matchingQuestion;
};

function WithQuestionSuggestion({
  node,
  children,
  enableQuestionSuggestions,
  flowId,
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const {state, dispatch} = useChatbotStateContext();

  const matchingQuestion = useQuestionSuggestion(node);

  const tipContent = React.useMemo(
    () => (
      <div>
        {`${t('Preset_question_available', 'Preset question available')}!`}
        <p>
          <Link
            onClick={() => {
              if (matchingQuestion) {
                dispatch({
                  type: 'CONVERT_TO_PRESET',
                  payload: {
                    nodeId: node.id,
                  },
                });
              }
              handleClose();
            }}
            color="clickable"
            underline={false}
          >{`${t(
            'Convert_to_preset_question',
            'Convert to preset question',
          )} →`}</Link>

          <br />
          <span className={css.ignoreSuggestions}>
            {`${t('or', 'or')},`}
            {'\u00A0'}
            <Link
              color="clickable"
              underline={false}
              onClick={() => {
                handleHideQuestionSuggestion(flowId, dispatch);
                handleClose();
              }}
            >
              {` ${t('ignore_all_suggestions', 'ignore all suggestions')}`}
            </Link>
          </span>
        </p>
      </div>
    ),
    [matchingQuestion],
  );
  const [componentRef, handleContextMenu, handleClose, isActive] =
    useContextTip({
      id: 'resuableIdForSuggestions',
      content: tipContent,
      fixedTo: 'left',
      arrowPosition: 'end',
    });

  const handleSuggestionContext = (evt: SyntheticMouseEvent<>) => {
    if (state.editingNode || state.insertingNode) {
      // do nothing
      return;
    }
    handleContextMenu(evt);
  };

  React.useEffect(() => {
    if (isActive && (state.editingNode || state.insertingNode)) {
      handleClose(); //make sure any open tips close while node is being edited
    }
  }, [state.editingNode, state.insertingNode]);

  if (!matchingQuestion || !enableQuestionSuggestions) {
    return children;
  } else {
    return (
      <div
        ref={componentRef}
        onContextMenu={handleContextMenu}
        onClick={handleClose}
        onMouseEnter={handleSuggestionContext}
        className={css.questionWithSuggestion}
      >
        {children}
      </div>
    );
  }
}

export function FeedbackNode({
  graph,
  style,
  editable,
  setShowFeedbackEditor,
  feedbackEnabled,
  setFlow,
  defaultGraph,
  readOnly,
  edgeError,
  nodeError,
  nodeWarning,
}: {
  graph: EditableGraph,
  style: {
    left: number,
    top: number,
  },
  editable: boolean,
  setShowFeedbackEditor: (boolean) => void,
  feedbackEnabled: boolean,
  setFlow?: (Graph) => void,
  defaultGraph: Graph,
  readOnly: boolean,
  edgeError?: Validation,
  nodeError?: Validation,
  nodeWarning?: Validation2,
}): React.Node {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();

  const handleUpdateFeedbackEnabled = () => {
    dispatch(
      updateFlowConfig(graph.id, {
        ...graph.flow_config,
        feedback_enabled: !feedbackEnabled,
      }),
    ).then((updatedFlowConfig) => {
      if (setFlow) {
        setFlow({...defaultGraph, flow_config: updatedFlowConfig});
      }
    });
  };

  const {canFlowUpdate} = useMutableFieldStatus(!editable, 'question');

  return (
    <>
      <div className={classify(css.node, css.nodeEnabled)} style={style}>
        <div className={css.title}>
          <span className={css.name}>{`${t('Feedback', 'Feedback')} ${
            feedbackEnabled ? '' : `(${t('Disabled', 'Disabled')})`
          }`}</span>
          <ContextMenu2
            className={css.edit}
            menuContent={
              <>
                <MenuOption onClick={() => setShowFeedbackEditor(true)}>
                  {canFlowUpdate
                    ? t('View_and_Edit', 'View and Edit')
                    : editable
                    ? t('Edit', 'Edit')
                    : t('View', 'View')}
                </MenuOption>
                {!readOnly && (
                  <MenuOption onClick={handleUpdateFeedbackEnabled}>
                    {feedbackEnabled
                      ? t('Disable', 'Disable')
                      : t('Enable', 'Enable')}
                  </MenuOption>
                )}
              </>
            }
          />
        </div>
        <div className={css.body}>
          <ReadOnlyDraftEditor
            entityType={graph.anchor_entity_type}
            isFirstSmsMessage={false}
            rte_json={defaultGraph.flow_config.feedback_question}
          />
        </div>
      </div>
    </>
  );
}

function defineErrors(conditionsErrors, nodeError, nodeWarning, t) {
  const error = [];
  const warning = [];

  if (conditionsErrors?.includes('EMPTY_CONDITION_SET')) {
    error.push(
      `${t(
        'There_are_no_conditions_that_lead_to_this_path_Add_a_condition_to_the_response_node_above_to_make_this_a_valid_path',
        'There are no conditions that lead to this path. Add a condition to the response node above to make this a valid path',
      )}.`,
    );
  } else if (conditionsErrors?.includes('IDENTICAL_CONDITION_SET')) {
    error.push(
      `${t(
        'This_path_has_shares_the_same_set_of_conditions_as_one_of_its_siblings_Add_or_remove_a_condition_to_make_it_valid',
        'This path has shares the same set of conditions as one of its siblings. Add or remove a condition to make it valid',
      )}.`,
    );
  }

  if (nodeError?.errors?.includes('INVALID_FIRST_MESSAGE')) {
    error.push(
      `${t(
        'To_avoid_carrier_filtering_The_first_message_in_a_conversation_must_be_a_question',
        'To avoid carrier filtering. The first message in a conversation must be a question',
      )}.`,
    );
  }

  if (hasError(nodeWarning, 'answers', 'MISSING_T2A_KEYWORD')) {
    warning.push(
      `${t(
        'This_node_includes_Text_to_Apply_keywords_that_have_not_been_added_to_Text_to_Apply_settings',
        'This node includes Text to Apply keywords that have not been added to Text to Apply settings',
      )}.`,
    );
  }

  if (hasError(nodeWarning, 'text_draft_json', 'DISCOVER_ATTR_WARNING')) {
    warning.push(
      `${t(
        'This_node_contains_job_match_variable_To_avoid_errors_when_using_a_job_match_variable_please_ensure_a_job_match_node_is_present_before_this_node',
        'This node contains job match variable. To avoid errors when using a job match variable, please ensure a job match node is present before this node',
      )}.`,
    );
  }

  if (hasError(nodeWarning, 'text_draft_json', 'NO_QUESTION_MARK')) {
    warning.push(
      `${t(
        'This_node_does_not_appear_to_include_a_question',
        'This node does not appear to include a question',
      )}.`,
    );
  }

  if (hasError(nodeWarning, 'text_draft_json', 'NO_LEAF_QUESTION')) {
    warning.push(
      `${t(
        'Last_node_cannot_be_a_question',
        'Last node cannot be a question',
      )}.`,
    );
  }

  if (hasError(nodeWarning, 'text_draft_json', 'NO_LEAF_MEETING_NODE')) {
    warning.push(
      `${t(
        'Last_node_cannot_be_a_meeting_node',
        'Last node cannot be a meeting node',
      )}.`,
    );
  }

  if (
    hasError(
      nodeWarning,
      'writeback_field_id',
      'MISSING_APPLICATION_DISPOSITION_VALUE',
    )
  ) {
    warning.push(
      `${t(
        'A_disposition_node_needs_a_valid_ATS_Writeback_Field',
        'A disposition node needs a valid ATS Writeback Field',
      )}`,
    );
  }

  if (hasError(nodeWarning, 'text_draft_json', 'SMS_RECOMMEND_QUESTION')) {
    warning.push(
      `${t(
        'To_avoid_deliverability_issues_for_SMS_touchpoints_we_recommend_beginning_with_a_question_node',
        'To avoid deliverability issues for SMS touchpoints, we recommend beginning with a question node',
      )}.`,
    );
  }

  if (nodeError?.errors?.includes('MISSING_PRECEDING_QUESTION')) {
    warning.push(
      `${t(
        'Live_Chat_flows_require_a_question_before_live_agent_transfer_can_begin',
        'Live Chat flows require a question before live agent transfer can begin',
      )}`,
    );
  }

  return {
    error,
    warning,
  };
}

function AlertList(props: {
  errors?: string[],
  warnings?: string[],
}): React.Node {
  return (
    <>
      <ul>
        {props.errors?.map((error) => (
          <li>{error}</li>
        ))}
        {props.warnings?.map((warning) => (
          <li>{warning}</li>
        ))}
      </ul>
    </>
  );
}

function NodeActions({node, disabled, hasMultipleChildren, dispatch}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const reduxDispatch = useDispatch();
  // $FlowFixMe[incompatible-call]: need to type node action props
  const {canFlowUpdate} = useMutableFieldStatus(disabled, node.type);

  return (
    <>
      {editableNodeTypes.includes(node.type) && (
        <MenuOption
          onClick={() =>
            dispatch({
              type: 'EDIT_NODE',
              payload: node.id,
            })
          }
        >
          {canFlowUpdate
            ? t('View_and_Edit', 'View and Edit')
            : disabled
            ? t('View', 'View')
            : t('Edit', 'Edit')}
        </MenuOption>
      )}
      {!disabled && (
        <MenuOption
          onClick={async () => {
            const yes =
              !hasMultipleChildren ||
              (await reduxDispatch(
                showGenericConfirm({
                  title: t('Node_Removal', 'Node Removal'),
                  text: `${t(
                    'Are_you_sure_you_want_to_delete_all_the_branches_below_this_node_If_not_remove_the_branches_you_wish_to_eliminate_and_try_removing_the_node_again',
                    'Are you sure you want to delete all the branches below this node? If not, remove the branches you wish to eliminate and try removing the node again',
                  )}.`,
                  confirmText: t('Remove', 'Remove'),
                }),
              ));
            if (yes) {
              dispatch({
                type: 'REMOVE_NODE',
                payload: node.id,
              });
            }
          }}
        >
          {t('Remove', 'Remove')}
        </MenuOption>
      )}
    </>
  );
}

function DraftTextBody({node, isSms, isFirstMessage}) {
  const body = React.useMemo(() => {
    // fallback for link to flow node because it doesn't have .text_draft_json
    if (node.type === 'link_to_flow') {
      return 'Link to external flow';
    }

    const contentJson =
      node.type === 'notification'
        ? node.notification_email_subject
        : node.text_draft_json != null
        ? node.text_draft_json
        : null;
    const text = contentJson ? convertFromRaw(contentJson).getPlainText() : '';

    return text;
  }, [node]);

  return (
    <div className={css.draftContent}>
      <div>{body}</div>
      {isFirstMessage && isSms && <OptOutMessage />}
    </div>
  );
}

function JobMatchBody({node}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const body = React.useMemo(() => {
    const contentJson = node.job_search_criteria_data.matching_messages_rte;
    const text = contentJson ? convertFromRaw(contentJson).getPlainText() : '';

    return text;
  }, [node]);

  return <div className={css.draftContent}>{body}</div>;
}

function DispositionBody({node, entityType}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const writebackAttributes = useWritebackAllowedAttributesV2(entityType);

  const {getWbFieldId} = useOWBFields();

  const fieldName = React.useMemo(
    () =>
      (node.writeback_field_id &&
        writebackAttributes?.attribute_meta_data?.find(
          (attribute) => getWbFieldId(attribute) === node.writeback_field_id,
        )?.attribute_name) ||
      String(node.writeback_field_id),
    [writebackAttributes, node],
  );

  return (
    <div style={{flexFlow: 'column'}}>
      <span>
        {`${t('Set_field', 'Set field')} `}
        <b>{fieldName}</b>
        {` ${t('to', 'to')} `}
      </span>
      {node.disposition_writeback_value ? (
        <span>{String(node.disposition_writeback_value)}</span>
      ) : node.text_draft_json != null ? (
        <ReadOnlyDraftEditor
          entityType={entityType}
          rte_json={node.text_draft_json}
          isFirstSmsMessage={false}
        />
      ) : null}
    </div>
  );
}

function ConditionBody({node, graph}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const limit = 2;
  const {
    condition_ruleset: {expressions},
  } = node;
  const operatorLabels = {
    true: {
      equals: t('is_exactly', 'is exactly'),
      in: t('is_one_of', 'is one of'),
      starts_with: t('starts_with', 'starts with'),
      ends_with: t('ends_with', 'ends with'),
      greater_than: t('is_greater_than', 'is greater than'),
      less_than: t('is_less_than', 'is less than'),
      contains: t('contains', 'contains'),
    },
    false: {
      equals: t('is_not', 'is not'),
      in: t('is_not_one_of', 'is not one of'),
      starts_with: t('does_not_start_with', 'does not start with'),
      ends_with: t('does_not_end_with', 'does not end with'),
      greater_than: t('is_less_than', 'is less than'),
      less_than: t('is_greater_than', 'is greater than'),
      contains: t('does_not_contain', 'does not contain'),
    },
  };
  return (
    <div className={css.conditions}>
      {expressions.slice(0, limit).map((expression, index) => (
        <div key={expression.id}>
          <span className={css.if}>
            {index === 0 ? t('IF', 'IF') : t('AND', 'AND')}
          </span>{' '}
          <span className={css.left}>
            {expression.dynamic_field ? (
              `<${expression.dynamic_field}>`
            ) : (
              <>
                “
                {graph.nodes.get(expression.node_id_answer)?.name ||
                  expression.node_id_answer}
                ”
              </>
            )}
          </span>{' '}
          <span className={css.operator}>
            {expression.is_not
              ? operatorLabels.false[expression.operator]
              : operatorLabels.true[expression.operator]}
          </span>{' '}
          <span className={css.right}>
            {expression.condition_value instanceof Array ? (
              expression.condition_value.length > 0 ? (
                bufferList(
                  expression.condition_value.map((value) => (
                    <span key={value}>'{value}'</span>
                  )),
                  <span>, </span>,
                  <span>{t('or', 'or')}</span>,
                )
              ) : (
                <span>{t('empty', 'empty')}</span>
              )
            ) : (
              <span>{`'${
                expression.condition_value || t('null', 'null')
              }'`}</span>
            )}
          </span>
        </div>
      ))}
      {expressions.length > limit && (
        <div>{`+ ${expressions.length - limit} ${t('more', 'more')}`}</div>
      )}
    </div>
  );
}

function SummaryBody({node, graph}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const parents = findParents(node, graph);

  const limit = 2;
  const {summary_items} = node;
  const question_nodes: ConversationNode[] = parents.filter(
    (node) => node.type === 'question',
  );
  return (
    <div className={css.conditions}>
      {question_nodes.slice(0, limit).map((node) => {
        const summary_item = summary_items.find(
          (item) => item.node_id === node.id,
        );
        return (
          <div key={node.id}>
            <span className={css.left}>
              “{summary_item?.short_name || node.name}”
            </span>
            <span className={css.operator}>:</span>{' '}
            <span className={css.right}>
              {summary_item?.enabled
                ? t('enabled', 'enabled')
                : t('disabled', 'disabled')}
            </span>
          </div>
        );
      })}
      {summary_items.length > limit && (
        <div>{`+ ${summary_items.length - limit} ${t('more', 'more')}`}</div>
      )}
    </div>
  );
}

function LATBody({node, graph}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const latQueueRequest = useApi<LATQueueEntry[]>('messages_v2/lat/queues');

  const queueId =
    node.additional_configs_json.queue_routing_rules?.default_queue_id;

  if (
    latQueueRequest.isLoading ||
    queueId == null ||
    !Array.isArray(latQueueRequest.result)
  ) {
    return null;
  }

  return (
    <div>
      {`${t('LAT_with_queue', 'LAT with queue')}: `}
      {latQueueRequest.result?.find((queue) => queue.id === queueId)?.name ??
        t('missing_name', 'missing name')}
    </div>
  );
}

function LinkToFlowBody({node, graph, flows, loaded}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const targetFlowId = node.additional_configs_json.target_flow_id;
  const targetFlow = flows.find((flow) => flow.id === targetFlowId);

  return (
    <div>
      {loaded && targetFlowId != null && (
        <RerouterLink
          to={`/conversations/${targetFlowId}`}
          target="blank"
          title={t(
            'open_this_flow_in_new_window',
            'open this flow in new window',
          )}
        >
          <strong>{targetFlow?.name}</strong>
        </RerouterLink>
      )}
    </div>
  );
}

function DynamicQuestionBody({graph, node}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  // These don't currently have any content
  const writebackAttributes = useWritebackAllowedAttributesV2(
    graph.anchor_entity_type,
  );
  const writebackField = writebackAttributes?.attribute_meta_data.find(
    (m) => m.field_id === node.writeback_field_id,
  );
  // This q&a node will write back to <strong>{}</strong>

  return (
    <div>
      {writebackField ? (
        <span>
          {t(
            'Dynamic_questions_writing_back_to',
            'Dynamic questions writing back to',
          )}{' '}
          <strong>{writebackField.display_name}</strong>
        </span>
      ) : (
        `${t(
          'Writebacks_not_yet_configured_for_this_qa_node',
          'Writebacks not yet configured for this q&a node',
        )}`
      )}
    </div>
  );
}
